import { Router, NavigationEnd } from "@angular/router";
import { DOCUMENT } from "@angular/common";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { ApiService } from 'src/app/authentication/otp/api.service';
import {
  Component,
  Inject,
  ElementRef,
  OnInit,
  Renderer2,
  HostListener,
  OnDestroy,
} from "@angular/core";
import { ROUTES } from "./sidebar-items-new";
import { RouteInfo } from "./sidebar.metadata";
import { Role } from "src/app/core/models/role";
import { TokenStorageService } from "src/app/core/service/token-storage.service";
import { PermissionService } from "src/app/core/service/permission.service";
import { ImageViewComponent } from "../image-view/image-view.component";
import { BaseComponent } from "src/app/shared/components/base/base.component";
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { AccountService } from "src/app/admin/modules/users/data/services/account.service";
import { AuthService } from "src/app/core/service/auth.service";
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LevelNameService } from "src/app/authentication/otp/level-name.service";
import { ChurchNamingService } from "src/app/core/service/church-naming.service";

@Component({
  selector: "app-sidebar",
  templateUrl: "./sidebar.component.html",
  styleUrls: ["./sidebar.component.scss"]
})
export class SidebarComponent extends BaseComponent implements OnInit, OnDestroy {
  public sidebarItems: RouteInfo[] = [];
  level1Menu = "";
  level2Menu = "";
  level3Menu = "";
  public innerHeight: any;
  public bodyTag: any;
  listMaxHeight: string;
  listMaxWidth: string;
  userFullName: string;
  userType: string;
  headerHeight = 60;
  routerObj = null;
  imageUrl: SafeResourceUrl;
  currentUser: any;
  churchColourCode: string | null = null;
  churchLogo: string = "assets/images/caritas-bank-logo.png";
  hierarchyDepth: number = 3;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private renderer: Renderer2,
    public elementRef: ElementRef,
    private dialog: MatDialog,
    private router: Router,
    private sanitizer: DomSanitizer,
    private accountService: AccountService,
    private authService: AuthService,
    private apiService: ApiService,
    private tokenStorageService: TokenStorageService,
    private levelNameService: LevelNameService,
    private churchNamingService: ChurchNamingService,
    private permissionService: PermissionService
  ) {
    super();
    this.routerObj = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.renderer.removeClass(this.document.body, "overlay-open");
        this.openActiveMenu(event.urlAfterRedirects);
      }
    });
  }
  @HostListener("window:resize", ["$event"])
  windowResizecall(event) {
    this.setMenuHeight();
    this.checkStatuForResize(false);
  }
  @HostListener("document:mousedown", ["$event"])
  onGlobalClick(event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.renderer.removeClass(this.document.body, "overlay-open");
    }
  }

  ngOnInit() {
    this.currentUser = this.tokenStorageService.getUser();
    if (this.currentUser) {
      this.initializeComponent();
    }
    this.initLeftSidebar();
    this.bodyTag = this.document.body;
  }

  initializeComponent() {
    // Navigation must be available while optional hierarchy details are loading.
    this.buildSidebar();
    const unitId = this.currentUser.parishId;
    if (!unitId) {
      return;
    }

    this.apiService.getSubLevelNames(unitId).pipe(
      catchError(err => {
        console.error("API call to getSubLevelNames failed:", err);
        this.buildSidebar();
        return of(null);
      })
    ).subscribe(unitDetails => {
      if (!unitDetails) {
        console.error("Received null details from API. Building sidebar with module permissions only.");
        this.buildSidebar();
        return;
      }

      const unitType = unitDetails.type;

      if (!unitType) {
        console.warn(`API response is missing the 'type' property. Building with module permissions only. Response:`, unitDetails);
      } else {
        console.log(`Unit type received from API: '${unitType}'. Building sidebar accordingly.`);
      }

      this.levelNameService.updateLevelNames(unitDetails);
      this.hierarchyDepth = unitDetails.grandchildLevelName ? 3 : 2;
      this.buildSidebar(unitType);
      this.updateSidebarLabels(this.levelNameService.getLevelNames());
      this.getChurchProfile();
      this.getData(this.currentUser.id);
    });
  }

  buildSidebar(unitType?: string) {
    this.userFullName = this.currentUser.userName;
    this.userType = this.permissionService.hasModule('CHURCH_DASHBOARD') ? Role.Admin : Role.User;

    this.sidebarItems = ROUTES.filter((route: RouteInfo) => {
      return this.hasAccess(route, unitType);
    }).map(item => {
      const clonedItem = JSON.parse(JSON.stringify(item));
      if (clonedItem.submenu && clonedItem.submenu.length > 0) {
        clonedItem.submenu = clonedItem.submenu.filter(sub => this.hasAccess(sub, unitType));
      }
      return clonedItem;
    }).filter(item => {
      return !(item.class === "menu-toggle" && item.submenu && item.submenu.length === 0);
    });

    this.updateServiceLabels();
    this.openActiveMenu(this.router.url);
  }

  private hasAccess(route: RouteInfo, unitType?: string): boolean {
    const unitTypeMatch = !route.unitType || route.unitType.length === 0 || 
                          (unitType && route.unitType.includes(unitType));
    
    if (!unitTypeMatch) return false;

    // Group titles should always be visible
    if (route.groupTitle) return true;

    if (route.permissions && route.permissions.length > 0) {
      const permissionMatch = route.requireAllPermissions
        ? route.permissions.every(item => this.permissionService.hasPermission(item.module, item.action))
        : route.permissions.some(item => this.permissionService.hasPermission(item.module, item.action));
      if (!permissionMatch) return false;
    }

    if (route.modules && route.modules.length > 0) {
      return route.requireAllModules 
        ? this.permissionService.hasAllModules(route.modules)
        : this.permissionService.hasAnyModule(route.modules);
    }

    return false;
  }

  updateSidebarLabels(levelNames: any) {
    if (!levelNames) return;

    const childLevelName = levelNames.childLevelName || 'Branch';
    const grandchildLevelName = levelNames.grandchildLevelName || 'Sub Branches';

    const outstationItem = this.findMenuItem(this.sidebarItems, 'outstation');
    const communityItem = this.findMenuItem(this.sidebarItems, 'community');

    if (outstationItem) outstationItem.title = childLevelName;
    if (communityItem) {
      communityItem.title = grandchildLevelName;
      communityItem.hidden = this.hierarchyDepth < 3;
    }

    const outstationMemberItem = this.findMenuItem(this.sidebarItems, 'members');
    const communityMemberItem = this.findMenuItem(this.sidebarItems, 'membersCommunity');

    if (outstationMemberItem) {
        outstationMemberItem.title = `Members per ${childLevelName}`;
    }
    if (communityMemberItem) {
        communityMemberItem.title = `Members per ${grandchildLevelName}`;
        communityMemberItem.hidden = this.hierarchyDepth < 3;
    }

    this.updateServiceLabels();
  }

  private updateServiceLabels(): void {
    const serviceItem = this.findMenuItem(this.sidebarItems, 'mass');
    const manageServicesItem = this.findMenuItem(this.sidebarItems, 'mass-service');
    const regularServicesItem = this.findMenuItem(this.sidebarItems, 'regular-services');

    if (serviceItem) {
      serviceItem.title = this.churchNamingService.getServiceLabel();
    }

    if (manageServicesItem) {
      manageServicesItem.title = this.churchNamingService.getManageServicesLabel();
    }

    if (regularServicesItem) {
      regularServicesItem.title = this.churchNamingService.getRegularServicesLabel();
    }
  }

  ngOnDestroy() { if (this.routerObj) this.routerObj.unsubscribe(); }
  initLeftSidebar() { this.setMenuHeight(); this.checkStatuForResize(true); }
  setMenuHeight() { this.innerHeight = window.innerHeight; const height = this.innerHeight - this.headerHeight; this.listMaxHeight = `${height}px`; this.listMaxWidth = "500px"; }
  checkStatuForResize(firstTime: any) { if (window.innerWidth < 1170) { this.renderer.addClass(this.document.body, "ls-closed"); } else { this.renderer.removeClass(this.document.body, "ls-closed"); } }
  mouseHover(e: any) { const body = this.elementRef.nativeElement.closest("body"); if (body.classList.contains("submenu-closed")) { this.renderer.addClass(this.document.body, "side-closed-hover"); this.renderer.removeClass(this.document.body, "submenu-closed"); } }
  mouseOut(e: any) { const body = this.elementRef.nativeElement.closest("body"); if (body.classList.contains("side-closed-hover")) { this.renderer.removeClass(this.document.body, "side-closed-hover"); this.renderer.addClass(this.document.body, "submenu-closed"); } }
  menuId(item: RouteInfo): string { return item.path || item.moduleName; }

  callLevel1Toggle(event: any, element: string) {
    this.level1Menu = this.level1Menu === element ? "" : element;
    this.level2Menu = "";
    this.level3Menu = "";
  }
  callLevel2Toggle(event: any, element: string) {
    this.level2Menu = this.level2Menu === element ? "" : element;
    this.level3Menu = "";
  }
  callLevel3Toggle(event: any, element: string) {
    this.level3Menu = this.level3Menu === element ? "" : element;
  }

  private openActiveMenu(url: string): void {
    const path = (url || '').split(/[?#]/)[0];
    const findBranch = (items: RouteInfo[]): RouteInfo[] => {
      for (const item of items) {
        if (item.hidden) continue;
        const children = findBranch(item.submenu || []);
        if (children.length) return [item, ...children];
        if (item.path && (path === item.path || path.startsWith(`${item.path}/`))) {
          return [item];
        }
      }
      return [];
    };
    const branch = findBranch(this.sidebarItems);
    this.level1Menu = branch[0] ? this.menuId(branch[0]) : "";
    this.level2Menu = branch[1] ? this.menuId(branch[1]) : "";
    this.level3Menu = branch[2] ? this.menuId(branch[2]) : "";
  }
  logout(): void { this.tokenStorageService.signOut(); this.router.navigate(["/authentication/signin"]); }

  openImageDialog(): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.width = "500px";
    dialogConfig.data = { currentUser: this.currentUser };
    const dialogRef = this.dialog.open(ImageViewComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.updatedUserData) {
        this.currentUser = result.updatedUserData;
      }
    });
  }

  private findMenuItem(items: any[], moduleName: string): any {
    for (const item of items) {
      if (item.moduleName === moduleName) return item;
      if (item.submenu?.length > 0) {
        const found = this.findMenuItem(item.submenu, moduleName);
        if (found) return found;
      }
    }
    return null;
  }

  getData(userId: string) {
    this.accountService.getuserById(userId).subscribe({
      next: (res) => {
        const user = res.entity;
        if(user && user.imageBanner) {
            const imageSrc = `data:image/jpeg;base64,${user.imageBanner}`;
            const blob = this.dataURItoBlob(imageSrc);
            const blobUrl = URL.createObjectURL(blob);
            this.imageUrl = this.sanitizer.bypassSecurityTrustResourceUrl(blobUrl);
        }
      }
    });
  }

  getChurchProfile(){
    const churchId = this.currentUser?.parishId;
    const hasChurchAdminAccess = this.permissionService.hasModule('CHURCH_DASHBOARD');
    
    if(churchId && hasChurchAdminAccess){
      this.authService.getChurchProfilebyId(churchId).subscribe({
        next: (data) => {
          if (data && data.entity) {
            this.churchColourCode = data.entity.colourCodes || '#fff';
            if (data.entity?.logo) {
              this.churchLogo = `data:image/jpeg;base64,${data.entity.logo}`;
            }
          }
        },
        error: (err) => {
          console.error('Error fetching church profile', err);
          this.churchColourCode = '#fff';
        }
      });
    } else {
      this.churchColourCode = '#fff';
    }
  }

  isCaritasLogo(): boolean {
    return this.churchLogo === "assets/images/caritas-bank-logo.png";
  }

  private dataURItoBlob(dataURI: string): Blob {
    const byteString = atob(dataURI.split(',')[1]);
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mimeString });
  }
}
